import { useEffect, useRef, useState, useCallback } from "react";
import { API } from "../services/api";

const HEARTBEAT_INTERVAL_MS = 30_000;
const IDLE_RELEASE_MS = 120_000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"];
const pendingReleaseByCommandeId = new Map();

function rememberPendingRelease(id, releasePromise) {
    pendingReleaseByCommandeId.set(id, releasePromise);
    releasePromise.finally(() => {
        if (pendingReleaseByCommandeId.get(id) === releasePromise) {
            pendingReleaseByCommandeId.delete(id);
        }
    });
}

async function waitForPendingRelease(id) {
    const pendingRelease = pendingReleaseByCommandeId.get(id);
    if (pendingRelease) await pendingRelease.catch(() => {});
}

export function useCommandeLock(commandeId) {
    const [hasLock, setHasLock] = useState(false);
    const [lockedBy, setLockedBy] = useState(null);
    const [idleReleased, setIdleReleased] = useState(false);
    const heartbeatRef = useRef(null);
    const idleTimerRef = useRef(null);
    const hasLockRef = useRef(false);
    const idleReleasedRef = useRef(false);
    const tryAcquireRef = useRef(null);
    hasLockRef.current = hasLock;

    const stopHeartbeat = useCallback(() => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    }, []);

    const clearIdleTimer = useCallback(() => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
    }, []);

    const sendRelease = useCallback((id) => {
        const releasePromise = API.delete(`/commandes/${id}/lock`, { noRedirectOn401: true });
        rememberPendingRelease(id, releasePromise);
        releasePromise.catch(() => {});
        return releasePromise;
    }, []);

    const releaseDueToInactivity = useCallback((id) => {
        if (!id || !hasLockRef.current) return;
        stopHeartbeat();
        clearIdleTimer();
        hasLockRef.current = false;
        idleReleasedRef.current = true;
        setHasLock(false);
        setLockedBy(null);
        setIdleReleased(true);
        sendRelease(id);
    }, [clearIdleTimer, sendRelease, stopHeartbeat]);

    const scheduleIdleRelease = useCallback((id) => {
        clearIdleTimer();
        if (!id || !hasLockRef.current) return;
        idleTimerRef.current = setTimeout(() => {
            releaseDueToInactivity(id);
        }, IDLE_RELEASE_MS);
    }, [clearIdleTimer, releaseDueToInactivity]);

    const startHeartbeat = useCallback((id) => {
        stopHeartbeat();
        heartbeatRef.current = setInterval(async () => {
            if (!hasLockRef.current) return;
            try {
                await API.put(`/commandes/${id}/lock`, {}, { noRedirectOn401: true });
            } catch {
                tryAcquireRef.current?.(id);
            }
        }, HEARTBEAT_INTERVAL_MS);
    }, [stopHeartbeat]);

    const tryAcquire = useCallback(async (id, options = {}) => {
        if (!id) return false;
        if (idleReleasedRef.current && !options.force) return false;
        try {
            await waitForPendingRelease(id);
            const res = await API.post(`/commandes/${id}/lock`, {}, { noRedirectOn401: true });
            hasLockRef.current = true;
            idleReleasedRef.current = false;
            setHasLock(true);
            setLockedBy(res.data.lockedBy);
            setIdleReleased(false);
            startHeartbeat(id);
            scheduleIdleRelease(id);
            return true;
        } catch (e) {
            hasLockRef.current = false;
            idleReleasedRef.current = false;
            setHasLock(false);
            setLockedBy(e.response?.data?.lockedBy || null);
            setIdleReleased(false);
            return false;
        }
    }, [scheduleIdleRelease, startHeartbeat]);

    tryAcquireRef.current = tryAcquire;

    useEffect(() => {
        if (!commandeId) return;
        const reacquireOnFocus = () => {
            tryAcquire(commandeId);
        };
        const reacquireOnVisibility = () => {
            if (document.visibilityState === "visible") reacquireOnFocus();
        };

        window.addEventListener("focus", reacquireOnFocus);
        document.addEventListener("visibilitychange", reacquireOnVisibility);
        return () => {
            window.removeEventListener("focus", reacquireOnFocus);
            document.removeEventListener("visibilitychange", reacquireOnVisibility);
        };
    }, [commandeId, tryAcquire]);

    useEffect(() => {
        if (!commandeId) return;
        const handleActivity = () => {
            if (idleReleasedRef.current) {
                idleReleasedRef.current = false;
                setIdleReleased(false);
                tryAcquireRef.current?.(commandeId, { force: true });
                return;
            }
            if (hasLockRef.current) scheduleIdleRelease(commandeId);
        };

        ACTIVITY_EVENTS.forEach(eventName => {
            window.addEventListener(eventName, handleActivity, { passive: true });
        });
        return () => {
            ACTIVITY_EVENTS.forEach(eventName => {
                window.removeEventListener(eventName, handleActivity);
            });
        };
    }, [commandeId, scheduleIdleRelease]);

    const release = useCallback(async (id) => {
        if (!id || !hasLockRef.current) return;
        stopHeartbeat();
        clearIdleTimer();
        setHasLock(false);
        setLockedBy(null);
        setIdleReleased(false);
        idleReleasedRef.current = false;
        try {
            const releasePromise = sendRelease(id);
            await releasePromise;
        } catch { /* best effort */ }
    }, [clearIdleTimer, sendRelease, stopHeartbeat]);

    // Acquire on mount / commandeId change
    useEffect(() => {
        if (!commandeId) return;
        setHasLock(false);
        setLockedBy(null);
        setIdleReleased(false);
        idleReleasedRef.current = false;
        tryAcquire(commandeId);

        return () => {
            stopHeartbeat();
            clearIdleTimer();
            idleReleasedRef.current = false;
            // On libère toujours le lock sans condition pour éviter la race condition
            // où hasLockRef.current serait encore false si la réponse du POST n'est
            // pas encore arrivée. Le backend ignore la demande si on ne tient pas le lock.
            sendRelease(commandeId);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commandeId]);

    return { hasLock, lockedBy, idleReleased, tryAcquire, setLockedBy, release };
}
